import React from 'react'
import { Badge, Button, Popover, Typography } from 'antd'
import { SettingOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface MappingWarningButtonProps {
  missingCount: number
}

/**
 * Compact entry point to customer/supplier mapping settings, replacing the previous inline
 * warning banner. Always visible; shows a dot while pairs are missing, and the details on click
 * instead of pushing them onto the page unconditionally.
 */
const MappingWarningButton: React.FC<MappingWarningButtonProps> = ({ missingCount }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const content = (
    <div style={{ maxWidth: 320 }}>
      {missingCount > 0 ? (
        <>
          <Typography.Paragraph strong style={{ marginBottom: 4 }}>
            {t(
              'icReconciliation:mappingWarningTitle',
              '{{count}} yritysparia vailla asiakas- tai toimittajalinkitystä',
              { count: missingCount }
            )}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 12 }}>
            {t(
              'icReconciliation:mappingWarningDescription',
              'Täsmäytystulokset voivat jäädä puutteellisiksi ilman linkityksiä. Täydennä puuttuvat parit konsernin asiakaslinkityksessä.'
            )}
          </Typography.Paragraph>
        </>
      ) : (
        <Typography.Paragraph style={{ marginBottom: 12 }}>
          {t('icReconciliation:mappingAllLinked', 'Kaikki yritysparit on linkitetty.')}
        </Typography.Paragraph>
      )}
      <Button size="small" type="primary" onClick={() => navigate('/settings/company/group/customer-mapping')}>
        {t('icReconciliation:mappingWarningAction', 'Täydennä linkitykset')}
      </Button>
    </div>
  )

  return (
    <Popover trigger="click" placement="bottomRight" content={content}>
      <Badge dot={missingCount > 0} offset={[-4, 4]}>
        <Button icon={<SettingOutlined />} title={t('icReconciliation:mappingSettingsButton', 'Linkitysasetukset')} />
      </Badge>
    </Popover>
  )
}

export default MappingWarningButton
